import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Button, Card, Space, Spin, Typography, Result, Divider, theme } from 'antd';
import { palette } from '../../theme/palette';
import { SafetyCertificateOutlined, CheckCircleOutlined } from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

/**
 * Portal público donde el contribuyente autoriza el uso de su e.firma.
 *
 * Sin login, por token, igual que el de autofactura: estas cuentas son de plan
 * vendido por lote y su titular puede no haber entrado nunca al sistema.
 *
 * Abrir esta página NO autoriza. Muestra qué se pide y exige un acto explícito:
 * un correo escaneado por un antivirus no puede equivaler a consentir.
 */
const EfirmaConsentPortal = () => {
  const { token: themeToken } = theme.useToken();
  const { token } = useParams();

  const [cargando, setCargando] = useState(true);
  const [autorizando, setAutorizando] = useState(false);
  const [error, setError] = useState('');
  const [datos, setDatos] = useState(null);
  const [autorizado, setAutorizado] = useState(null);

  useEffect(() => {
    consultar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const consultar = async () => {
    if (!token) return;
    setCargando(true);
    setError('');

    try {
      const resp = await fetch(`/api/autorizar-efirma/${encodeURIComponent(token)}`, {
        headers: { Accept: 'application/json' },
      });
      const json = await resp.json();

      if (!resp.ok) {
        setError(json?.error || 'Este enlace no es válido o ya fue utilizado');
        setDatos(null);
        return;
      }

      setDatos(json?.data || null);
    } catch (e) {
      setError('No se pudo cargar la solicitud. Revisa tu conexión.');
    } finally {
      setCargando(false);
    }
  };

  const autorizar = async () => {
    setAutorizando(true);

    try {
      const resp = await fetch(`/api/autorizar-efirma/${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      });
      const json = await resp.json();

      if (!resp.ok) {
        setError(json?.error || 'No se pudo registrar tu autorización');
        return;
      }

      setAutorizado(json?.data || {});
    } catch (e) {
      setError('No se pudo registrar tu autorización. Revisa tu conexión.');
    } finally {
      setAutorizando(false);
    }
  };

  const marco = (contenido) => (
    <div style={{ minHeight: '100vh', background: themeToken.colorBgLayout, padding: '24px 16px' }}>
      <div style={{ maxWidth: 560, margin: '0 auto' }}>{contenido}</div>
    </div>
  );

  if (cargando) {
    return marco(
      <Card>
        <Space direction="vertical" align="center" style={{ width: '100%' }}>
          <Spin size="large" />
          <Text type="secondary">Cargando la solicitud…</Text>
        </Space>
      </Card>
    );
  }

  if (autorizado) {
    return marco(
      <Card>
        <Result
          status="success"
          icon={<CheckCircleOutlined style={{ color: palette.fiscalAccent }} />}
          title="Autorización registrada"
          subTitle={
            <Space direction="vertical" size={4}>
              <Text>Gracias. A partir de ahora podemos usar tu e.firma para atender tus obligaciones fiscales ante el SAT.</Text>
              {autorizado.autorizado_en && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Registrado el {autorizado.autorizado_en}
                </Text>
              )}
            </Space>
          }
        />
        <Divider />
        <Text type="secondary" style={{ fontSize: 12 }}>
          Puedes retirar esta autorización cuando quieras contactándonos. Al hacerlo, dejaremos
          de poder utilizar tu e.firma de inmediato.
        </Text>
      </Card>
    );
  }

  if (error) {
    return marco(
      <Card>
        <Result
          status="warning"
          title="Enlace no válido"
          subTitle={error}
          extra={
            <Text type="secondary" style={{ fontSize: 13 }}>
              Los enlaces de autorización sirven una sola vez y caducan. Si ya autorizaste, no
              necesitas hacer nada más. Si no, pide uno nuevo.
            </Text>
          }
        />
      </Card>
    );
  }

  return marco(
    <Card>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space align="start">
          <SafetyCertificateOutlined style={{ fontSize: 28, color: palette.fiscalAccent }} />
          <div>
            <Title level={4} style={{ margin: 0 }}>Autoriza el uso de tu e.firma</Title>
            {datos?.cliente && <Text type="secondary">{datos.cliente}</Text>}
          </div>
        </Space>

        <Paragraph style={{ marginBottom: 0 }}>
          Registramos tu <strong>e.firma (FIEL)</strong> para poder atender tus obligaciones
          fiscales. Antes de utilizarla en tu nombre ante el SAT necesitamos tu autorización
          expresa.
        </Paragraph>

        <Alert
          type="info"
          showIcon
          message="Qué estás autorizando"
          description={
            <Space direction="vertical" size={4}>
              <Text>Que resguardemos tu e.firma de forma cifrada.</Text>
              <Text>
                Que la utilicemos únicamente para presentar tus declaraciones y realizar
                consultas ante el SAT por tu cuenta.
              </Text>
              {datos?.alcance && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Alcance registrado: {datos.alcance}
                </Text>
              )}
            </Space>
          }
        />

        <Button
          type="primary"
          size="large"
          block
          loading={autorizando}
          onClick={autorizar}
          style={{ background: palette.fiscalAccent, borderColor: palette.fiscalAccent }}
        >
          Autorizo el uso de mi e.firma
        </Button>

        {/* No autorizar es una opción real, no un callejón sin salida */}
        <Text type="secondary" style={{ fontSize: 13, textAlign: 'center', display: 'block' }}>
          Si no autorizas, cierra esta página. Tu e.firma permanece resguardada y nadie podrá
          utilizarla en tu nombre.
        </Text>

        {datos?.vence && (
          <Text type="secondary" style={{ fontSize: 12, textAlign: 'center', display: 'block' }}>
            Este enlace es personal, sirve una sola vez y vence el {datos.vence}.
          </Text>
        )}
      </Space>
    </Card>
  );
};

export default EfirmaConsentPortal;
